import { createContext, useCallback, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { PreviewableEntity } from 'src/types/PreviewableEntity';
import { PreviewableEntityType } from 'src/components/EntityViewWithPreview';
import TetherApi from 'src/lib/tether-microservices/TetherApi';

const getEntityKey = (type: PreviewableEntityType, id: string) => `${type}${id}`;

interface EntityPreviewContextValue {
  getEntity(type: PreviewableEntityType, id: string): Promise<PreviewableEntity>;
  /** Drops a cached entity so the next preview refetches it — call after editing the entity. */
  invalidateEntity(type: PreviewableEntityType, id: string): void;
}

interface EntityPreviewProps {
  children: ReactNode;
}

const EntityPreviewContext = createContext<EntityPreviewContextValue>({
  getEntity: () => new Promise<PreviewableEntity>(null),
  invalidateEntity: () => {},
});

/**
 * One in-flight request per entity, shared by every row that names it.
 *
 * The cache is a REF, not reducer state, and that is the whole point. It used to be state
 * updated by a dispatch, so the entry was only visible after a re-render — and a table's
 * rows all mount in the SAME commit, which is exactly when they all ask. Every row found
 * the cache empty and started its own request: a device-assignment panel listing ten
 * devices in one organisation made ten identical `getOrganisationById` calls, and a page
 * of fifty properties made fifty.
 *
 * A ref is read and written synchronously, so the second caller in a commit finds the
 * first one's promise. Nothing renders off this map, so there is nothing for it to be
 * state FOR.
 *
 * A rejected promise stays cached deliberately: the alternative is that a failing entity
 * is retried by every row that mentions it, on every render.
 */
export const EntityPreviewProvider: FC<EntityPreviewProps> = (props) => {
  const { children } = props;
  const entityPromises = useRef<Record<string, Promise<PreviewableEntity>>>({});

  const getEntity = useCallback((type: PreviewableEntityType, id: string): Promise<PreviewableEntity> => {
    const key = getEntityKey(type, id);

    const existing = entityPromises.current[key];
    if (existing) {
      return existing;
    }

    let promise: Promise<PreviewableEntity>;

    switch (type) {
      case 'customer':
        promise = TetherApi.getCustomerById(id);
        break;
      case 'device':
        promise = TetherApi.getDevice(id);
        break;
      case 'installation':
        promise = TetherApi.getInstallationById(id);
        break;
      case 'installationLocation':
        promise = TetherApi.getInstallationLocationById(id);
        break;
      case 'organisation':
        promise = TetherApi.getOrganisationById(id);
        break;
      default:
        throw new Error(`Type ${type} not supported`);
    }

    entityPromises.current[key] = promise;

    return promise;
  }, []);

  const invalidateEntity = useCallback((type: PreviewableEntityType, id: string) => {
    delete entityPromises.current[getEntityKey(type, id)];
  }, []);

  return (
    <EntityPreviewContext.Provider value={{ getEntity, invalidateEntity }}>
      {children}
    </EntityPreviewContext.Provider>
  );
};

EntityPreviewProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default EntityPreviewContext;
